import { createContext, useContext, ReactNode } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { useAtom } from 'jotai';
import { toastsAtom, ToastMessage } from '@/atoms/toast';
import { cn } from '@/lib/utils';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';

export const ToastContext = createContext<{
  showToast: (message: ToastMessage) => void;
}>({
  showToast: () => {},
});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useAtom(toastsAtom);

  const showToast = (message: ToastMessage) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { ...message, id }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      <Toast.Provider swipeDirection="right">
        {children}
        {toasts.map((toast) => (
          <Toast.Root
            key={toast.id}
            className={cn(
              'fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-lg border p-4 shadow-lg',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full',
              toast.variant === 'success' && 'border-green-500/20 bg-green-500/10',
              toast.variant === 'error' && 'border-red-500/20 bg-red-500/10',
              toast.variant === 'info' && 'border-blue-500/20 bg-blue-500/10'
            )}
          >
            {toast.variant === 'success' && (
              <CheckCircle className="h-5 w-5 text-green-400" />
            )}
            {toast.variant === 'error' && (
              <AlertCircle className="h-5 w-5 text-red-400" />
            )}
            {toast.variant === 'info' && (
              <Info className="h-5 w-5 text-blue-400" />
            )}
            <Toast.Description className="text-sm text-white">
              {toast.message}
            </Toast.Description>
            <Toast.Close asChild>
              <button className="ml-auto rounded-md p-1 text-gray-400 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </Toast.Close>
          </Toast.Root>
        ))}
        <Toast.Viewport />
      </Toast.Provider>
    </ToastContext.Provider>
  );
}